// src/pages/tour-packages/hooks/useTourPackageCreateForm.ts

import { useEffect, useRef, type BaseSyntheticEvent } from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  type UseFormReturn,
  type UseFieldArrayReturn,
} from "react-hook-form";

import type {
  CreateTourWithDetailsPayload,
  MealPlan,
  TourStatus,
} from "@/api/features/tours";
import type { TourItineraryDayInput } from "@/api/features/tours/types";
import {
  buildRatePrices,
  getMissingModePriceFields,
  ALL_MODE_PRICE_FIELDS,
} from "./tourRatePricing";

/* =========================================
 * Form value type – UI-friendly
 * ======================================= */

export type TourPackageFormValues = {
  tour: {
    name: string;
    tourTypeId: number | "";
    supplierId: number | "";
    nights: number | "";
    days: number | "";
    hasFixedDepartures: boolean;
    isPrivateTour: boolean;
    tourMode: "GROUP" | "PRIVATE";
    isMultiCity: boolean;
    cityCount: number | "";
    currencyCode: string;
    status: TourStatus;
  };
  tourRates: {
    rateType: "BASE" | "PROMO";
    status: "ACTIVE" | "INACTIVE";
    priority: number | "";
    travelFrom: string;
    travelTo: string;
    bookFrom: string;
    bookTo: string;
    currencyCode: string;
    priceDouble: number | "";
    priceTriple: number | "";
    priceChildWb: number | "";
    priceChildWob: number | "";
    priceInfant: number | "";
    price2Pax: number | "";
    price3Pax: number | "";
    price4Pax: number | "";
    price5Pax: number | "";
    price6Pax: number | "";
    isTaxInclusive: boolean;
    label: string;
    notes: string;
  }[];
  cities: {
    countryCode: string;
    country: string;
    state: string;
    city: string;
    nightsInCity: number | "";
  }[];
  itineraryDays: {
    dayNumber: number | "";
    title: string;
    description: string;
  }[];
  inclusions: {
    sortOrder: number | "";
    label: string;
    description?: string;
  }[];
  exclusions: {
    sortOrder: number | "";
    label: string;
    description?: string;
  }[];
  cancellationPolicies: {
    label: string;
    description?: string;
  }[];
  hotels: {
    hotelName: string;
    hotelCategory: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    country: string;
    countryCode: string;
    postalCode: string;
    roomType: string;
    mealPlan: MealPlan;
  }[];
  images: {
    imageUrl: string;
    caption: string;
    sortOrder: number | "";
  }[];
  departures: {
    departureDate: string;
    capacity: number | "";
    status: string;
  }[];
};

/* =========================================
 * Hook options & return types
 * ======================================= */

export type UseTourPackageCreateFormOptions<TResult = unknown> = {
  defaultValues?: Partial<CreateTourWithDetailsPayload>;
  onSubmit: (payload: CreateTourWithDetailsPayload) => Promise<TResult>;
};

export type UseTourPackageCreateFormResult = {
  form: UseFormReturn<TourPackageFormValues>;
  handleSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;

  citiesFieldArray: UseFieldArrayReturn<TourPackageFormValues, "cities", "id">;
  tourRatesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "tourRates",
    "id"
  >;
  itineraryDaysFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "itineraryDays",
    "id"
  >;
  inclusionsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "inclusions",
    "id"
  >;
  exclusionsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "exclusions",
    "id"
  >;
  cancellationPoliciesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "cancellationPolicies",
    "id"
  >;
  hotelsFieldArray: UseFieldArrayReturn<TourPackageFormValues, "hotels", "id">;
  imagesFieldArray: UseFieldArrayReturn<TourPackageFormValues, "images", "id">;
  departuresFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "departures",
    "id"
  >;
};

/* =========================================
 * Main hook
 * ======================================= */

export function useTourPackageCreateForm<TResult = unknown>(
  options: UseTourPackageCreateFormOptions<TResult>
): UseTourPackageCreateFormResult {
  const { defaultValues, onSubmit } = options;

  const form = useForm<TourPackageFormValues>({
    defaultValues: buildDefaultFormValues(defaultValues),
    shouldFocusError: false,
  });

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    formState,
    setError,
    clearErrors,
    setValue,
  } = form;

  const citiesFieldArray = useFieldArray({ control, name: "cities" });
  const tourRatesFieldArray = useFieldArray({ control, name: "tourRates" });
  const itineraryDaysFieldArray = useFieldArray({
    control,
    name: "itineraryDays",
  });
  const inclusionsFieldArray = useFieldArray({ control, name: "inclusions" });
  const exclusionsFieldArray = useFieldArray({ control, name: "exclusions" });
  const cancellationPoliciesFieldArray = useFieldArray({
    control,
    name: "cancellationPolicies",
  });
  const hotelsFieldArray = useFieldArray({ control, name: "hotels" });
  const imagesFieldArray = useFieldArray({ control, name: "images" });
  const departuresFieldArray = useFieldArray({ control, name: "departures" });

  const isSubmitting = formState.isSubmitting;

  /* =========================================
   * Derived fields: nights -> days (auto)
   * + keep itineraryDays length in sync
   * ======================================= */

  const nightsWatch = useWatch({ control, name: "tour.nights" });
  const daysWatch = useWatch({ control, name: "tour.days" });

  const didInitDerivedRef = useRef(false);

  // nights -> days (days = nights + 1)
  useEffect(() => {
    // Avoid doing anything before RHF hydrates defaultValues properly
    // (still allows subsequent user changes)
    if (!didInitDerivedRef.current) {
      didInitDerivedRef.current = true;
    }

    if (nightsWatch === "" || nightsWatch == null) {
      setValue("tour.days", "" as any, { shouldDirty: true, shouldValidate: false });
      return;
    }

    const nightsNum = Number(nightsWatch);
    if (!Number.isFinite(nightsNum) || nightsNum <= 0) {
      setValue("tour.days", "" as any, { shouldDirty: true, shouldValidate: false });
      return;
    }

    const nextDays = nightsNum + 1;

    // Only set if different to avoid re-render loops
    const currentDaysNum =
      daysWatch === "" || daysWatch == null ? null : Number(daysWatch);

    if (currentDaysNum !== nextDays) {
      setValue("tour.days", nextDays as any, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [nightsWatch]); // intentionally not depending on daysWatch to reduce loops

  // days -> itineraryDays length sync (append/remove to match)
  useEffect(() => {
    const raw = daysWatch;
    const daysNum = raw === "" || raw == null ? 0 : Number(raw);

    if (!Number.isFinite(daysNum) || daysNum <= 0) {
      // If days cleared, you can choose to clear itinerary.
      // Keeping it simple: clear it.
      if ((itineraryDaysFieldArray.fields?.length ?? 0) > 0) {
        itineraryDaysFieldArray.replace([]);
      }
      return;
    }

    const target = Math.max(1, Math.floor(daysNum));
    const current = itineraryDaysFieldArray.fields?.length ?? 0;

    // Append missing days
    if (current < target) {
      for (let i = current; i < target; i++) {
        itineraryDaysFieldArray.append(
          {
            dayNumber: (i + 1) as any,
            title: "",
            description: "",
          },
          { shouldFocus: false }
        );
      }
    }

    // Remove extra days (from end)
    if (current > target) {
      for (let i = current - 1; i >= target; i--) {
        itineraryDaysFieldArray.remove(i);
      }
    }

    // Ensure dayNumber stays consistent for existing items
    const finalLen = target;
    for (let i = 0; i < finalLen; i++) {
      setValue(`itineraryDays.${i}.dayNumber` as const, (i + 1) as any, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [daysWatch]);

  const handleSubmit = rhfHandleSubmit(async (values) => {
    const payload = buildPayloadFromFormValues(values);
    // Reset the errors we manage here so fixed fields clear.
    clearErrors("root.server");
    clearErrors("tour.currencyCode");
    clearErrors(ALL_MODE_PRICE_FIELDS.map((k) => `tourRates.0.${k}` as any));

    let hasError = false;

    // Currency is required.
    if (!String(values.tour.currencyCode ?? "").trim()) {
      setError("tour.currencyCode", {
        type: "required",
        message: "Select a currency.",
      });
      hasError = true;
    }

    // All price fields for the active mode are required — flag each missing one
    // so it appears in the error list and gets highlighted in red.
    for (const key of getMissingModePriceFields(values)) {
      setError(`tourRates.0.${key}` as any, {
        type: "required",
        message: "Required",
      });
      hasError = true;
    }

    if (hasError) return;

    try {
      await onSubmit(payload);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save tour package. Please try again.";

      setError("root.server", {
        type: "server",
        message,
      });
    }
  });

  return {
    form,
    handleSubmit,
    isSubmitting,
    citiesFieldArray,
    tourRatesFieldArray,
    itineraryDaysFieldArray,
    inclusionsFieldArray,
    exclusionsFieldArray,
    cancellationPoliciesFieldArray,
    hotelsFieldArray,
    imagesFieldArray,
    departuresFieldArray,
  };
}

/* =========================================
 * Helpers – defaults + payload mapping
 * ======================================= */

function buildDefaultFormValues(
  defaults?: Partial<CreateTourWithDetailsPayload>
): TourPackageFormValues {
  return {
    tour: {
      name: defaults?.tour?.name ?? "",
      tourTypeId: defaults?.tour?.tourTypeId ?? "",
      supplierId: defaults?.tour?.supplierId ?? "",
      nights: defaults?.tour?.nights ?? "",
      days: defaults?.tour?.days ?? "",
      // Trip type defaults to Fixed departures (the toggle always has one active).
      hasFixedDepartures: defaults?.tour?.hasFixedDepartures ?? true,
      isPrivateTour: defaults?.tour?.isPrivateTour ?? false,
      tourMode: defaults?.tour?.tourMode ?? "GROUP",
      isMultiCity: defaults?.tour?.isMultiCity ?? false,
      cityCount: defaults?.tour?.cityCount ?? "",
      currencyCode: defaults?.tour?.currencyCode ?? "",
      status: defaults?.tour?.status ?? "DRAFT",
    },
    tourRates:
      defaults?.tour?.tourRates?.map((r: any) => ({
        rateType: (r?.rateType ?? "BASE") as "BASE" | "PROMO",
        status: (r?.status ?? "ACTIVE") as "ACTIVE" | "INACTIVE",
        priority: r?.priority ?? 0,
        travelFrom: r?.travelFrom ? String(r.travelFrom).slice(0, 10) : "",
        travelTo: r?.travelTo ? String(r.travelTo).slice(0, 10) : "",
        bookFrom: r?.bookFrom ? String(r.bookFrom).slice(0, 10) : "",
        bookTo: r?.bookTo ? String(r.bookTo).slice(0, 10) : "",
        currencyCode:
          typeof r?.currencyCode === "string" && r.currencyCode.trim()
            ? String(r.currencyCode).trim().toUpperCase()
            : (defaults?.tour?.currencyCode ?? ""),
        priceDouble: r?.priceDouble ?? "",
        priceTriple: r?.priceTriple ?? "",
        priceChildWb: r?.priceChildWb ?? "",
        priceChildWob: r?.priceChildWob ?? "",
        priceInfant: r?.priceInfant ?? "",
        price2Pax: r?.price2Pax ?? "",
        price3Pax: r?.price3Pax ?? "",
        price4Pax: r?.price4Pax ?? "",
        price5Pax: r?.price5Pax ?? "",
        price6Pax: r?.price6Pax ?? "",
        isTaxInclusive:
          typeof r?.isTaxInclusive === "boolean"
            ? r.isTaxInclusive
            : defaults?.tour?.isTaxInclusive ?? true,
        label: r?.label ?? "",
        notes: r?.notes ?? "",
      })) ?? [
        {
          rateType: "BASE",
          status: "ACTIVE",
          priority: 0,
          travelFrom: "",
          travelTo: "",
          bookFrom: "",
          bookTo: "",
          currencyCode: defaults?.tour?.currencyCode ?? "",
          priceDouble: "",
          priceTriple: "",
          priceChildWb: "",
          priceChildWob: "",
          priceInfant: "",
          price2Pax: "",
          price3Pax: "",
          price4Pax: "",
          price5Pax: "",
          price6Pax: "",
          isTaxInclusive: defaults?.tour?.isTaxInclusive ?? true,
          label: "",
          notes: "",
        },
      ],

    cities:
      defaults?.cities?.map((c) => ({
        countryCode: (c as any).countryCode ?? "",
        country: c.country ?? "",
        state: c.state ?? "",
        city: c.city ?? "",
        nightsInCity: (c.nightsInCity as number | undefined) ?? "",
      })) ?? [{ countryCode: "", country: "", state: "", city: "", nightsInCity: "" }],

    itineraryDays:
      defaults?.itineraryDays?.map((d) => ({
        dayNumber: d.dayNumber ?? "",
        title: d.title ?? "",
        description: d.description ?? "",
      })) ?? [],

    inclusions:
      defaults?.inclusions?.map((i) => ({
        sortOrder: i.sortOrder ?? "",
        label: i.label ?? "",
        description: i.description ?? "",
      })) ?? [],

    exclusions:
      defaults?.exclusions?.map((e) => ({
        sortOrder: e.sortOrder ?? "",
        label: e.label ?? "",
        description: e.description ?? "",
      })) ?? [],

    cancellationPolicies:
      defaults?.cancellationPolicies?.map((c) => ({
        label: c.label ?? "",
        description: c.description ?? "",
      })) ?? [],

    hotels:
      defaults?.hotels?.map((h) => ({
        hotelName: h.hotelName ?? "",
        hotelCategory: h.hotelCategory ?? "",
        addressLine1: h.addressLine1 ?? "",
        addressLine2: h.addressLine2 ?? "",
        city: h.city ?? "",
        state: h.state ?? "",
        country: h.country ?? "",
        countryCode: h.countryCode ?? "",
        postalCode: h.postalCode ?? "",
        roomType: h.roomType ?? "",
        mealPlan: h.mealPlan ?? "BB",
      })) ?? [],

    images:
      defaults?.images?.map((img) => ({
        imageUrl: img.imageUrl ?? "",
        caption: img.caption ?? "",
        sortOrder: img.sortOrder ?? "",
      })) ?? [],

    departures:
      defaults?.departures?.map((d: any) => {
        const raw = d?.departureDate;
        return {
          departureDate: raw ? String(raw).slice(0, 10) : "",
          capacity: d?.capacity ?? "",
          status: d?.status ?? "ACTIVE",
        };
      }) ?? [],
  };
}

function buildPayloadFromFormValues(
  values: TourPackageFormValues
): CreateTourWithDetailsPayload {
  const itineraryDaysPayload: TourItineraryDayInput[] = values.itineraryDays
    .map((d) => {
      const dayNumber = Number(d.dayNumber || 0);

      return {
        dayNumber,
        title: d.title.trim() || undefined,
        description: d.description.trim() || undefined,
      } satisfies TourItineraryDayInput;
    })
    .filter((d) => d.dayNumber > 0 && (d.title || d.description));

  const cityCountDerived =
    typeof values.tour.cityCount === "number"
      ? values.tour.cityCount
      : Number(values.tour.cityCount || values.cities.length || 1);

  const departuresPayload = values.departures
    .map((d) => {
      const rawDate: any = d.departureDate;
      const departureDate = normalizeToYmd(rawDate);

      if (!departureDate) return null;

      const capacity =
        d.capacity === "" || d.capacity == null ? null : Number(d.capacity);

      const status = String(d.status || "ACTIVE").trim().toUpperCase();

      return { departureDate, capacity, status };
    })
    .filter(Boolean) as Array<{
    departureDate: string;
    capacity: number | null;
    status: string | null;
  }>;

  // For fixed-departure tours the Travel window is hidden in the UI; derive the
  // rate's travel window from the FD dates so the rate (and its pricing) persists.
  const isFD = values.tour.hasFixedDepartures;
  const sortedDepartureDates = departuresPayload
    .map((d) => d.departureDate)
    .filter(Boolean)
    .sort();
  const fdTravelFrom = sortedDepartureDates[0] ?? "";
  const fdTravelTo = sortedDepartureDates[sortedDepartureDates.length - 1] ?? "";

  return {
    tour: {
      name: values.tour.name.trim(),
      tourTypeId: Number(values.tour.tourTypeId),
      supplierId: Number(values.tour.supplierId),
      nights: Number(values.tour.nights),
      days: Number(values.tour.days),
      hasFixedDepartures: values.tour.hasFixedDepartures,
      isPrivateTour: values.tour.isPrivateTour,
      tourMode: values.tour.tourMode,
      isMultiCity: values.tour.isMultiCity,
      cityCount: cityCountDerived,
      currencyCode: values.tour.currencyCode.trim().toUpperCase(),
      tourRates: values.tourRates
        .map((r) => ({
          rateType: r.rateType,
          status: r.status,
          priority:
            r.priority === "" || r.priority == null ? 0 : Number(r.priority),
          travelFrom: safeTrim(r.travelFrom) || (isFD ? fdTravelFrom : ""),
          travelTo:
            (safeTrim(r.travelTo) || (isFD ? fdTravelTo : "")) || null,
          bookFrom: safeTrim(r.bookFrom) || null,
          bookTo: safeTrim(r.bookTo) || null,
          // See useTourPackageEditForm.ts for why: no per-rate currency
          // control exists, so the header field is the only source of truth.
          currencyCode: safeUpper(values.tour.currencyCode),
          ...buildRatePrices(r, values.tour.tourMode),
          isTaxInclusive: r.isTaxInclusive,
          label: safeTrim(r.label) || null,
          notes: safeTrim(r.notes) || null,
        }))
        .filter((r) => r.travelFrom && r.priceDouble > 0),
      status: values.tour.status,
    },

    cities: values.cities
      .map((c, index) => ({
        countryCode: safeUpper((c as any).countryCode) || undefined,
        country: safeTrim((c as any).country),
        state: safeTrim((c as any).state),
        city: safeTrim((c as any).city),
        nightsInCity: c.nightsInCity ? Number(c.nightsInCity) : undefined,
        sortOrder: index + 1,
      }))
      .filter((c) => c.city || c.country),

    itineraryDays: itineraryDaysPayload,

    inclusions: values.inclusions
      .filter((i) => i.label.trim())
      .map((i, index) => ({
        sortOrder: index + 1,
        label: i.label.trim(),
        description: i.description?.trim() || undefined,
      })),

    exclusions: values.exclusions
      .filter((e) => e.label.trim())
      .map((e, index) => ({
        sortOrder: index + 1,
        label: e.label.trim(),
        description: e.description?.trim() || undefined,
      })),

    cancellationPolicies: values.cancellationPolicies
      .filter((c) => c.label.trim())
      .map((c, index) => ({
        sortOrder: index + 1,
        label: c.label.trim(),
        description: c.description?.trim() || undefined,
      })),

    hotels: values.hotels
      .map((h) => ({
        countryCode: h.countryCode?.trim().toUpperCase() || undefined,
        hotelName: h.hotelName.trim(),
        hotelCategory: h.hotelCategory.trim(),
        addressLine1: h.addressLine1.trim(),
        addressLine2: h.addressLine2.trim(),
        city: h.city.trim(),
        state: h.state.trim(),
        country: h.country.trim(),
        postalCode: h.postalCode.trim(),
        roomType: h.roomType.trim(),
        mealPlan: h.mealPlan,
      }))
      .filter((h) => h.hotelName),

    images: values.images
      .map((img, index) => ({
        imageUrl: img.imageUrl.trim(),
        caption: img.caption.trim(),
        sortOrder: img.sortOrder ? Number(img.sortOrder) : index + 1,
      }))
      .filter((img) => img.imageUrl),

    departures: values.tour.hasFixedDepartures ? departuresPayload : undefined,
  };
}

function safeTrim(v: unknown): string {
  return typeof v === "string" ? v.trim() : String(v ?? "").trim();
}

function safeUpper(v: unknown): string {
  return safeTrim(v).toUpperCase();
}

function normalizeToYmd(value: unknown): string {
  if (value == null) return "";

  const asString = safeTrim(value);
  if (!asString) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;

  const date =
    value instanceof Date
      ? value
      : new Date(asString);

  if (Number.isNaN(date.getTime())) return asString;
  return date.toISOString().slice(0, 10);
}
